import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import ShieldImg from "../../Assets/Icons/shield.png";

const FSHomeLive = () => {
  const [liveUtakmice, setLiveUtakmice] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLiveUtakmice = async () => {
      try {
        // 1. Dohvaćamo live utakmice (koristimo tournament_id prema tvojoj shemi)
        const [regularRes, veteranRes] = await Promise.all([
          supabase
            .from("turniri_utakmice")
            .select(
              "id, scorea, scoreb, is_live, tournament_id, team_a_id, team_b_id",
            )
            .eq("is_live", true),
          supabase
            .from("turniri_veterani")
            .select(
              "id, scorea, scoreb, is_live, tournament_id, team_a_id, team_b_id",
            )
            .eq("is_live", true),
        ]);

        const regularUtakmice = regularRes.data || [];
        const veteranUtakmice = (veteranRes.data || []).map((u) => ({
          ...u,
          isVeteran: true,
        }));
        const sveUtakmice = [...regularUtakmice, ...veteranUtakmice];

        if (sveUtakmice.length === 0) {
          setLoading(false);
          return;
        }

        // 2. Izvuci jedinstvene ID-eve turnira i timova (za obje tablice timova)
        const turnirIds = [
          ...new Set(sveUtakmice.map((u) => u.tournament_id).filter(Boolean)),
        ];
        const teamIds = [
          ...new Set(
            sveUtakmice
              .filter((u) => !u.isVeteran)
              .flatMap((u) => [u.team_a_id, u.team_b_id])
              .filter(Boolean),
          ),
        ];
        const veteranTeamIds = [
          ...new Set(
            sveUtakmice
              .filter((u) => u.isVeteran)
              .flatMap((u) => [u.team_a_id, u.team_b_id])
              .filter(Boolean),
          ),
        ];

        let turniriMap = {};
        let turniriNazivMap = {};
        let timoviMap = {};

        // 3. Dohvati turnire preko tournament_id da imamo slug
        if (turnirIds.length > 0) {
          const { data: turniriData } = await supabase
            .from("admin_turniri")
            .select("id, slug, naziv")
            .in("id", turnirIds);

          if (turniriData) {
            turniriMap = turniriData.reduce((acc, t) => {
              acc[t.id] = t.slug;
              turniriNazivMap[t.id] = t.naziv;
              return acc;
            }, {});
          }
        }

        // 4. Dohvati regularne timove
        if (teamIds.length > 0) {
          const { data: timoviData } = await supabase
            .from("turniri_timovi")
            .select("id, name, logo_url")
            .in("id", teamIds);

          if (timoviData) {
            timoviData.forEach((tim) => {
              timoviMap[tim.id] = tim;
            });
          }
        }

        // 5. Dohvati veteranske timove
        if (veteranTeamIds.length > 0) {
          const { data: vetTimoviData } = await supabase
            .from("turniri_veterani_timovi")
            .select("id, name, logo_url")
            .in("id", veteranTeamIds);

          if (vetTimoviData) {
            vetTimoviData.forEach((tim) => {
              timoviMap[tim.id] = tim;
            });
          }
        }

        // 6. Spoji sve podatke zajedno
        const enrichedUtakmice = sveUtakmice.map((utakmica) => ({
          ...utakmica,
          turnir_slug: turniriMap[utakmica.tournament_id] || null,
          turnir_naziv: turniriNazivMap[utakmica.tournament_id] || "Turnir",
          tim_a: timoviMap[utakmica.team_a_id] || null,
          tim_b: timoviMap[utakmica.team_b_id] || null,
        }));

        setLiveUtakmice(enrichedUtakmice);
      } catch (err) {
        console.error("Greška pri dohvatu live utakmica:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLiveUtakmice();

    const channelUtakmice = supabase
      .channel("fshome-live-utakmice-channel")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "turniri_utakmice" },
        (payload) => {
          if (payload.eventType === "UPDATE" && payload.new.is_live === false) {
            // Ako je utakmica ugašena, odmah je makni iz state-a
            setLiveUtakmice((prev) =>
              prev.filter((u) => u.id !== payload.new.id),
            );
          } else {
            // Inače osvježi podatke (ako je nova ili je promijenjen rezultat)
            fetchLiveUtakmice();
          }
        },
      )
      .subscribe();

    const channelVeterani = supabase
      .channel("fshome-live-veterani-channel")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "turniri_veterani" },
        (payload) => {
          if (payload.eventType === "UPDATE" && payload.new.is_live === false) {
            // Ako je veteranska utakmica ugašena, odmah je makni iz state-a
            setLiveUtakmice((prev) =>
              prev.filter((u) => u.id !== payload.new.id),
            );
          } else {
            // Inače osvježi podatke
            fetchLiveUtakmice();
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channelUtakmice);
      supabase.removeChannel(channelVeterani);
    };
  }, []);

  if (loading || liveUtakmice.length === 0) {
    return null;
  }

  const handleNavigation = (slug, tournamentId) => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (slug) {
      navigate(`/${slug}`);
    } else if (tournamentId) {
      navigate(`/turnir/${tournamentId}`);
    } else {
      console.warn("Nema ni slug-a ni tournament_id-ja za ovu utakmicu!");
    }
  };

  return (
    <div className="fs-live-section">
      <div className="fs-live-header">
        <div className="fs-live-title">
          <span className="fs-live-title-line" />
          <h3>Utakmice uživo</h3>
        </div>

        <div className="fs-live-status">
          <span />
          UŽIVO
        </div>
      </div>

      <div className="fs-live-list">
        {liveUtakmice.map((utakmica) => (
          <article
            key={utakmica.id}
            className="fs-live-card"
            onClick={() =>
              handleNavigation(utakmica.turnir_slug, utakmica.tournament_id)
            }
          >
            <div className="fs-live-card-top">
              <div className="fs-live-badge">
                <span />
                UŽIVO
              </div>

              {utakmica.isVeteran && (
                <div className="fs-live-veterani">VETERANI</div>
              )}

              <button
                type="button"
                className="fs-live-follow"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigation(
                    utakmica.turnir_slug,
                    utakmica.tournament_id,
                  );
                }}
              >
                Prati
                <i className="fi fi-rs-angle-small-right" />
              </button>
            </div>

            <div className="fs-live-match">
              <div className="fs-live-team">
                <div className="fs-live-team-logo">
                  <img
                    src={utakmica.tim_a?.logo_url || ShieldImg}
                    alt={utakmica.tim_a?.name || "Domaćin"}
                  />
                </div>

                <strong>{utakmica.tim_a?.name || "Domaćin"}</strong>
              </div>

              <div className="fs-live-score-area">
                <div className="fs-live-score">
                  <span>{utakmica.scorea ?? 0}</span>
                  <b>:</b>
                  <span>{utakmica.scoreb ?? 0}</span>
                </div>

                <small>UTAKMICA U TIJEKU</small>
              </div>

              <div className="fs-live-team">
                <div className="fs-live-team-logo">
                  <img
                    src={utakmica.tim_b?.logo_url || ShieldImg}
                    alt={utakmica.tim_b?.name || "Gost"}
                  />
                </div>

                <strong>{utakmica.tim_b?.name || "Gost"}</strong>
              </div>
            </div>

            <div className="fs-live-footer">
              <i className="fa-solid fa-trophy" />
              <span>{utakmica.turnir_naziv}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default FSHomeLive;
